// lib/woCalendar.mjs — R115 maint (Jesse A2: "all scheduled WOs on the calendar
// + a bucket of UNSCHEDULED WOs to drag-and-drop onto dates"). The pure
// selectors behind the calendar hub's maintenance layer: which open work
// orders are booked inside a window, which have no day yet (the 📥 bucket),
// per-home counts for the row badge, the view's KPI numbers, and the money
// projection rule for the WO detail. Shared by the server layer provider
// (server/calendarLayers/wo_unscheduled.js, via dynamic import) and the
// browser (client/src/pages/calendar/maint/*), so the two never disagree on
// "overdue" or "unscheduled". Pure ESM, no DOM, no I/O.
//
// Rules (from the R115 calendar-maint map + DECISIONS):
//   · open = status not done/cancelled and not deleted ('cancelled' is only
//     ever filtered — never written — but stays excluded everywhere);
//   · booked = scheduled_date, else the legacy scheduled_window start (a PM
//     vacancy window spans start→end); unscheduled = open with neither;
//   · due = effective due: a checkout-tracked WO (due_source 'checkout') is due
//     at the listing's NEXT checkout (computed live, never stored), anything
//     else at its stored due_date; overdue = due before today;
//   · unassigned = no assigned_to AND no vendor_id;
//   · money (cost / payer / billed / skill, invoice + quote amounts, owner
//     portal amounts) never rides a list or layer payload, and the detail
//     strips it for every role outside admin / director / accounting.
import { schedOf, schedEndOf, dueOf, priorityOf, laToday } from './woFormat.mjs';

export const WO_MONEY_ROLES = Object.freeze(['admin', 'director', 'accounting']);
/** Keys a non-money role may never send on a calendar edit (POST /work-orders/:id surface:'calendar' → 403). */
export const WO_MONEY_KEYS = Object.freeze(['cost', 'payer', 'billing_status', 'skill']);
export const PRIORITY_RANK = Object.freeze({ emergency: 0, urgent: 1, routine: 2, pm: 3 });
/** "Due soon" horizon for the Operations view's WOs-due stat (days from today, inclusive). */
export const DUE_SOON_DAYS = 7;

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const dayOf = (v) => { const s = String(v == null ? '' : v).slice(0, 10); return DAY.test(s) ? s : null; };
// R123 fix: the PACIFIC calendar day of a timestamp (every `today` here is the Pacific business day) —
// slicing the ISO string took the UTC day, so a WO opened after ~5pm PT read a day too young. A bare
// YYYY-MM-DD is already a day (never shifted); a Hostaway "YYYY-MM-DD HH:mm:ss" stamp is UTC.
const STAMP = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
export function bizDayOfStamp(v) {
  const s = String(v == null ? '' : v).trim();
  if (!s) return null;
  if (DAY.test(s)) return s;
  const t = Date.parse(STAMP.test(s) ? s.replace(' ', 'T') + 'Z' : s);
  return Number.isFinite(t) ? laToday(new Date(t)) : null;
}
const MS = 86400000;
export function addDaysStr(d, n) {
  const t = new Date(String(d) + 'T00:00:00Z');
  if (isNaN(t.getTime())) return null;
  t.setUTCDate(t.getUTCDate() + (Number(n) || 0));
  return t.toISOString().slice(0, 10);
}
export function diffDays(a, b) {
  const x = Date.parse(String(a) + 'T00:00:00Z'), y = Date.parse(String(b) + 'T00:00:00Z');
  return Number.isFinite(x) && Number.isFinite(y) ? Math.round((y - x) / MS) : null;
}

export function canSeeWoMoney(role) { return WO_MONEY_ROLES.includes(String(role || '')); }

/** Open = drawable: not done / cancelled / deleted, and it names a listing. */
export function isOpenWo(w) {
  return !!(w && w.id && w.listing_id != null && String(w.listing_id) !== '' && !w.deleted && w.status !== 'done' && w.status !== 'cancelled');
}

/** The booked span (inclusive) — scheduled_date is one day; a legacy multi-day window keeps its end. */
export function bookedSpan(w) {
  const start = schedOf(w);
  if (!start) return null;
  const end = schedEndOf(w) || start;
  return { start, end: end < start ? start : end };
}

/** listing_id → the earliest upcoming checkout (≥ today) from ao_turns; orphaned / finished cleans skipped. */
export function nextCheckoutIndex(turns, today, doneStatuses = ['cleaned', 'verified', 'ready']) {
  const out = new Map();
  for (const t of (Array.isArray(turns) ? turns : [])) {
    if (!t || t.orphaned || !t.listing_id || doneStatuses.includes(t.status)) continue;
    const co = dayOf(t.check_out);
    if (!co || (today && co < today)) continue;
    const k = String(t.listing_id);
    if (!out.has(k) || co < out.get(k)) out.set(k, co);
  }
  return out;
}

/** Effective due day: checkout-tracked → effective_due (or the next checkout from an index), else due_date. */
export function effectiveDueOf(w, nextCheckout) {
  if (!w) return null;
  if (w.due_source === 'checkout') {
    const live = dayOf(w.effective_due) || (nextCheckout && typeof nextCheckout.get === 'function' ? (nextCheckout.get(String(w.listing_id)) || null) : null);
    return live || null;
  }
  return dayOf(dueOf(w).date);
}

const vendorNameOf = (vendorNames, id) => {
  if (id == null || id === '' || !vendorNames) return null;
  const v = typeof vendorNames.get === 'function' ? vendorNames.get(String(id)) : vendorNames[String(id)];
  return v ? String(v).trim() || null : null;
};
export function vendorNameMap(vendors) {
  return new Map((Array.isArray(vendors) ? vendors : []).filter(v => v && v.id != null).map(v => [String(v.id), String(v.name || '').trim()]));
}

/** Per-WO flags every count and chip uses. */
export function woFlags(w, { today, nextCheckout } = {}) {
  const span = bookedSpan(w);
  const due = effectiveDueOf(w, nextCheckout);
  const prio = priorityOf(w);
  return {
    booked: !!span, span,
    due,
    overdue: !!(due && today && due < today),
    urgent: prio === 'emergency' || prio === 'urgent',
    unassigned: !String(w && w.assigned_to || '').trim() && !(w && w.vendor_id),
    was_booked: !!(span && today && span.end < today),
    due_soon: !!(due && today && due <= addDaysStr(today, DUE_SOON_DAYS)),
    priority: prio,
  };
}

/** The bucket row — NO money, no description body, no photos/comments bodies (counts only). */
export function shapeUnscheduled(w, { today, vendorNames, nextCheckout } = {}) {
  const f = woFlags(w, { today, nextCheckout });
  const created = bizDayOfStamp(w.created_at);   // R123 fix: the Pacific day it was opened
  const vendor = vendorNameOf(vendorNames, w.vendor_id);
  const items = Array.isArray(w.items) ? w.items : [];
  return {
    id: String(w.id), listing_id: String(w.listing_id),
    listing_name: String(w.listing_name || w.listing_id || '').slice(0, 120),
    title: String(w.title || '').trim().slice(0, 200) || (w.type === 'pm' ? 'PM' : 'Work order'),
    kind: w.type === 'pm' ? 'pm' : 'wo',
    status: String(w.status || 'open'),
    priority: f.priority,
    who: vendor || (String(w.assigned_to || '').trim().slice(0, 80) || null),
    role: ['maintenance', 'cleaner', 'inspector'].includes(w.role) ? w.role : 'maintenance',
    source: w.source ? String(w.source).slice(0, 40) : null,
    created_by: w.created_by ? String(w.created_by).slice(0, 80) : null,
    created_at: w.created_at || null,
    due_date: f.due,
    due_source: w.due_source === 'checkout' ? 'checkout' : null,
    age_days: created && today ? Math.max(0, diffDays(created, today) || 0) : null,
    comments_n: (Array.isArray(w.comments) ? w.comments.length : 0) + (Array.isArray(w.internal_notes) ? w.internal_notes.length : 0),
    photos_n: Array.isArray(w.photos) ? w.photos.length : 0,
    items_n: items.length, items_done: items.filter(i => i && i.done).length,
    unassigned: f.unassigned, overdue: f.overdue, urgent: f.urgent,
    guest_ok: !!(w.guest_permission && w.guest_permission.granted),
    was_booked: null,   // R115 review: the past day a missed booking had ('was booked Oct 13 — rebook')
  };
}

/** Bucket order: priority, then the nearest due day (none last), then oldest first. */
export function sortUnscheduled(a, b) {
  const pr = (PRIORITY_RANK[a.priority] ?? 2) - (PRIORITY_RANK[b.priority] ?? 2);
  if (pr) return pr;
  const ad = a.due_date || '9999-99-99', bd = b.due_date || '9999-99-99';
  if (ad !== bd) return ad < bd ? -1 : 1;
  const ac = String(a.created_at || ''), bc = String(b.created_at || '');
  if (ac !== bc) return ac < bc ? -1 : 1;
  return String(a.id).localeCompare(String(b.id));
}

const keepOf = (keep) => (keep == null ? null : (keep instanceof Set ? keep : new Set((Array.isArray(keep) ? keep : []).map(String))));
const inView = (span, from, to) => !!(span && (!from || span.end >= from) && (!to || span.start <= to));

/**
 * Split the open WOs: booked (span overlaps [from, to]), due_only (no day, effective due inside the
 * window — the dashed chip), unscheduled (no day at all, portfolio-wide). keep = listing ids (null = all).
 */
export function bucketWos(wos, { from, to, today, keep, vendorNames, nextCheckout } = {}) {
  const k = keepOf(keep);
  const booked = [], due_only = [], unscheduled = [];
  for (const w of (Array.isArray(wos) ? wos : [])) {
    if (!isOpenWo(w)) continue;
    if (k && !k.has(String(w.listing_id))) continue;
    const span = bookedSpan(w);
    // R115 review: an open WO whose booked day has PASSED and is outside the window is a missed booking — it goes
    // back in the bucket (flagged was_booked) instead of vanishing from both the timeline and the bucket
    if (span) {
      if (inView(span, from, to)) booked.push(w);
      else if (today && span.end < today) unscheduled.push({ ...shapeUnscheduled(w, { today, vendorNames, nextCheckout }), was_booked: span.start });
      continue;
    }
    unscheduled.push(shapeUnscheduled(w, { today, vendorNames, nextCheckout }));
    const due = effectiveDueOf(w, nextCheckout);
    if (due && (!from || due >= from) && (!to || due <= to)) due_only.push(w);
  }
  unscheduled.sort(sortUnscheduled);
  return { booked, due_only, unscheduled };
}

const zero = () => ({ open: 0, unscheduled: 0, booked: 0, booked_in_view: 0, overdue: 0, urgent: 0, unassigned: 0, due_soon: 0, was_booked: 0, pm: 0 });
/** Portfolio counts (the view's stats) + per-listing counts (the row badge) in ONE pass. */
export function woCounts(wos, { from, to, today, keep, nextCheckout } = {}) {
  const k = keepOf(keep);
  const total = zero();
  const by_listing = {};
  for (const w of (Array.isArray(wos) ? wos : [])) {
    if (!isOpenWo(w)) continue;
    const lid = String(w.listing_id);
    if (k && !k.has(lid)) continue;
    const f = woFlags(w, { today, nextCheckout });
    const row = by_listing[lid] || (by_listing[lid] = { open: 0, unscheduled: 0, booked_in_view: 0, overdue: 0, urgent: 0, due_soon: 0 });
    const bump = (key) => { total[key]++; if (key in row) row[key]++; };
    bump('open');
    const missed = f.booked && f.was_booked && !inView(f.span, from, to);   // in the bucket (see bucketWos)
    if (f.booked && !missed) { total.booked++; if (inView(f.span, from, to)) bump('booked_in_view'); if (f.was_booked) total.was_booked++; }
    else { bump('unscheduled'); if (missed) { total.booked++; total.was_booked++; } }
    if (f.overdue) bump('overdue');
    if (f.urgent) bump('urgent');
    if (f.unassigned) total.unassigned++;
    if (f.due_soon) bump('due_soon');
    if (f.priority === 'pm' || w.type === 'pm') total.pm++;
  }
  return { counts: total, by_listing };
}

/** The whole layer payload for GET /api/stays/calendar/layers?l=wo_unscheduled (no money, no contact fields). */
export function buildWoLayer(wos, { from, to, today, vendors, turns } = {}) {
  const needsCheckout = (Array.isArray(wos) ? wos : []).some(w => isOpenWo(w) && w.due_source === 'checkout' && !w.effective_due);
  const nextCheckout = needsCheckout ? nextCheckoutIndex(turns, today) : null;
  const vendorNames = vendorNameMap(vendors);
  const { unscheduled } = bucketWos(wos, { from, to, today, vendorNames, nextCheckout });
  const { counts, by_listing } = woCounts(wos, { from, to, today, nextCheckout });
  return {
    as_of: today || null, from: from || null, to: to || null,
    unscheduled, by_listing, counts,
    basis: 'ao_work_orders: open = not done/cancelled/deleted; unscheduled = no booked day (scheduled_date or window); overdue = effective due (next checkout when checkout-tracked) before today; unassigned = no person and no vendor. Portfolio-wide — not narrowed by the calendar search.',
  };
}

/** Group bucket rows by home (most rows first, then name). */
export function groupByListing(items) {
  const m = new Map();
  for (const it of (Array.isArray(items) ? items : [])) {
    const k = String(it.listing_id);
    if (!m.has(k)) m.set(k, { listing_id: k, listing_name: it.listing_name || k, items: [] });
    m.get(k).items.push(it);
  }
  return [...m.values()].sort((a, b) => (b.items.length - a.items.length) || String(a.listing_name).localeCompare(String(b.listing_name)));
}

/** A bucket row as a WO-like record for lib/woSearch.mjs buildDoc (slim rows search the same way the board does). */
export function searchableWo(it) {
  return {
    id: it.id, title: it.title, listing_name: it.listing_name, assigned_to: it.who || '', vendor_id: null,
    priority: it.priority, type: it.kind === 'pm' ? 'pm' : 'wo', role: it.role, created_by: it.created_by || '',
    created_at: it.created_at, due_date: it.due_source === 'checkout' ? null : it.due_date, effective_due: it.due_source === 'checkout' ? it.due_date : null,
    status: it.status, scheduled_date: null,
  };
}

/** Which money keys a request body carries. */
export function moneyKeysIn(body) {
  if (!body || typeof body !== 'object') return [];
  return WO_MONEY_KEYS.filter(k => Object.prototype.hasOwnProperty.call(body, k) && body[k] !== undefined);
}

/**
 * The WO detail for a role that may not see money: cost / payer / billed / skill removed; invoice,
 * quote and owner-portal amounts removed (+ the owner-portal notes, which quote them); the documents
 * and states stay; the vendor report stays. R115 review: the LOG is projected too — the edit route writes
 * "cost → 253.5; payer → owner; billed → os" and the vendor portal "$350.00 quote …" into its notes, so the
 * amounts / money fields in a note are redacted (the event itself stays). Returns a NEW object (never mutates
 * the stored record) flagged money_hidden.
 */
const MONEY_FIELD_RE = /\b(cost|payer|billed|skill)\s*→\s*[^;—()]*/gi;
const MONEY_AMT_RE = /\$\s?[\d,]+(?:\.\d+)?/g;
export function redactMoneyNote(s) {
  if (typeof s !== 'string') return s;
  return s.replace(MONEY_FIELD_RE, (_m, f) => `${f.toLowerCase()} updated `).replace(MONEY_AMT_RE, '$—').replace(/\s+;/g, ';').replace(/\s{2,}/g, ' ').trim();
}
export function stripWoMoney(w) {
  if (!w || typeof w !== 'object') return w;
  const out = { ...w };
  for (const k of WO_MONEY_KEYS) delete out[k];
  if (Array.isArray(w.invoices)) out.invoices = w.invoices.map(i => { if (!i || typeof i !== 'object') return i; const { total, lines, amount, subtotal, tax, ...rest } = i; return rest; });
  if (Array.isArray(w.quotes)) out.quotes = w.quotes.map(q => {
    if (!q || typeof q !== 'object') return q;
    const { total, lines, amount, ...rest } = q;
    if (rest.ai_check && typeof rest.ai_check === 'object') { const { expected_range, ...ai } = rest.ai_check; rest.ai_check = ai; }
    return rest;
  });
  // the owner-portal notes are free text that quote the amount (the QuickBooks one-click note reads
  // "…charge (Vendor, $120.00)") — the STATE stays, the notes go with the amounts
  if (w.owner_portal && typeof w.owner_portal === 'object') { const { amount, approved_amount, note, decision_note, ...op } = w.owner_portal; out.owner_portal = op; }
  if (w.vendor_progress && typeof w.vendor_progress === 'object' && 'amount' in w.vendor_progress) { const { amount, ...vp } = w.vendor_progress; out.vendor_progress = vp; }
  if (Array.isArray(w.log)) out.log = w.log.map(l => (l && typeof l === 'object' ? { ...l, ...(typeof l.note === 'string' ? { note: redactMoneyNote(l.note) } : {}), ...(typeof l.action === 'string' ? { action: redactMoneyNote(l.action) } : {}) } : l));
  out.money_hidden = true;
  return out;
}

// ── R123: the Issues board's dispatch projection ─────────────────────────────
// woDispatch(w, { today, vendorNames, nextCheckout }) → the slim, flat record
// the Issues board rides on row.dispatch (the stage ladder in lib/issueStage
// reads it; the fact line, the WO card and the popover header draw it). Every
// value is a primitive (string / number / boolean / null) — counts, days and
// states only. HARD DENYLIST: never cost / payer / billing_status / skill,
// invoices, quotes or any amount, the owner_portal object (its STATE only),
// vendor phone / email / vendor_link, access codes, the description, comment
// or log bodies. The keys are fixed (WO_DISPATCH_KEYS); lib/woDispatch.test.mjs
// deep-scans the output against WO_DISPATCH_DENY. due_soon mirrors woFlags
// (it includes an overdue due day; the stage flags pick one of the two).
export const WO_DISPATCH_KEYS = Object.freeze([
  'id', 'status', 'priority', 'role', 'title', 'who', 'who_kind', 'assigned_to', 'vendor_id', 'vendor_name',
  'booked', 'booked_end', 'was_booked', 'due', 'due_source', 'overdue', 'due_soon', 'urgent',
  'items_n', 'items_done', 'all_ticked', 'comments_n', 'photos_n', 'after_n', 'owner_state', 'quote_pending',
  'followup', 'vendor_done', 'timer_on', 'guest_ok', 'age_days', 'last_at',
]);
/** Keys that may never appear anywhere in a dispatch record (money, contact, access, free text). */
export const WO_DISPATCH_DENY = Object.freeze([
  ...WO_MONEY_KEYS, 'invoices', 'quotes', 'amount', 'approved_amount', 'total', 'subtotal', 'tax', 'lines', 'expected_range',
  'owner_portal', 'vendor_phone', 'vendor_email', 'phone', 'email', 'vendor_link', 'link', 'token',
  'access', 'access_code', 'door_code', 'lock_code', 'gate_code', 'code', 'wifi', 'wifi_password',
  'description', 'comments', 'internal_notes', 'notes', 'note', 'text', 'body', 'log', 'summary',
  'completion', 'vendor_progress', 'guest_permission', 'time_entries', 'total_minutes', 'photos', 'photos_meta', 'items',
]);
const STATE_RE = /^[a-z_]{1,24}$/;
const latestAt = (...lists) => {
  let best = null, bestT = -Infinity;
  for (const v of lists.flat()) {
    if (!v) continue;
    const t = Date.parse(String(v));
    if (Number.isFinite(t) && t > bestT) { bestT = t; best = String(v).slice(0, 40); }
  }
  return best;
};
const atsOf = (arr) => (Array.isArray(arr) ? arr.map(x => (x && typeof x === 'object' ? x.at : null)) : []);

export function woDispatch(w, { today, vendorNames, nextCheckout } = {}) {
  if (!w || typeof w !== 'object' || w.id == null || String(w.id) === '') return null;
  const f = woFlags(w, { today, nextCheckout });
  const items = Array.isArray(w.items) ? w.items.filter(i => i && typeof i === 'object') : [];
  const itemsDone = items.filter(i => i.done).length;
  const assigned = String(w.assigned_to == null ? '' : w.assigned_to).trim().slice(0, 80) || null;
  const vendorId = w.vendor_id != null && String(w.vendor_id).trim() !== '' ? String(w.vendor_id).slice(0, 80) : null;
  const vendorName = vendorId ? (vendorNameOf(vendorNames, vendorId) || '').slice(0, 80) || null : null;
  const who = vendorName || assigned || (vendorId ? 'Vendor' : null);
  const created = bizDayOfStamp(w.created_at);   // R123 fix: the Pacific day it was opened
  const op = w.owner_portal && typeof w.owner_portal === 'object' ? String(w.owner_portal.state || '') : '';
  const afterUrls = new Set((Array.isArray(w.photos_meta) ? w.photos_meta : []).filter(m => m && typeof m === 'object' && m.kind === 'after' && m.url).map(m => String(m.url)));
  return {
    id: String(w.id),
    status: String(w.status || 'open').slice(0, 24),
    priority: String(f.priority || 'routine').slice(0, 24),
    role: ['maintenance', 'cleaner', 'inspector'].includes(w.role) ? w.role : 'maintenance',
    title: String(w.title || '').trim().slice(0, 120) || (w.type === 'pm' ? 'PM' : 'Work order'),
    who,
    who_kind: vendorName || (!assigned && vendorId) ? 'vendor' : assigned ? 'person' : null,
    assigned_to: assigned,
    vendor_id: vendorId,
    vendor_name: vendorName,
    booked: f.span ? f.span.start : null,
    booked_end: f.span ? f.span.end : null,
    was_booked: f.was_booked,
    due: f.due,
    due_source: w.due_source === 'checkout' ? 'checkout' : null,
    overdue: f.overdue,
    due_soon: f.due_soon,
    urgent: f.urgent,
    items_n: items.length,
    items_done: itemsDone,
    all_ticked: w.status !== 'done' && items.length > 0 && itemsDone === items.length,
    comments_n: (Array.isArray(w.comments) ? w.comments.length : 0) + (Array.isArray(w.internal_notes) ? w.internal_notes.length : 0),
    photos_n: Array.isArray(w.photos) ? w.photos.length : 0,
    after_n: afterUrls.size,
    owner_state: STATE_RE.test(op) ? op : null,
    quote_pending: Array.isArray(w.quotes) && w.quotes.some(q => q && typeof q === 'object' && q.status === 'pending'),
    followup: !!(w.completion && typeof w.completion === 'object' && w.completion.needs_followup),
    vendor_done: !!(w.vendor_progress && typeof w.vendor_progress === 'object' && w.vendor_progress.all_done),
    timer_on: !!w.timer_started_at,
    guest_ok: !!(w.guest_permission && typeof w.guest_permission === 'object' && w.guest_permission.granted),
    age_days: created && today ? Math.max(0, diffDays(created, today) || 0) : null,
    last_at: latestAt([w.updated_at, w.created_at, w.completed_at], atsOf(w.log), atsOf(w.comments), atsOf(w.internal_notes)),
  };
}
